import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-secondary hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>

        <h1 className="text-2xl font-bold mb-2">Politique de confidentialité</h1>
        <p className="text-secondary text-sm mb-10">Dernière mise à jour : 23 février 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed text-gray-300">

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              Carmin (« nous », « notre », « nos ») est une plateforme de gestion digitale
              destinée aux professionnels de l'automobile, exploitée depuis la Belgique.
              La présente politique de confidentialité décrit comment nous collectons, utilisons,
              stockons et protégeons vos données personnelles conformément au Règlement Général
              sur la Protection des Données (RGPD) et à la législation belge applicable.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Responsable du traitement</h2>
            <p>
              Le responsable du traitement des données est Carmin, accessible via
              l'adresse <a href="https://carmin.app" className="text-carmin hover:underline">carmin.app</a>.
              Pour toute question relative à vos données personnelles, vous pouvez nous
              contacter à : <a href="mailto:privacy@carmin.app" className="text-carmin hover:underline">privacy@carmin.app</a>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Données collectées</h2>
            <p className="mb-3">Nous collectons les catégories de données suivantes :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Données d'identification :</strong> nom, prénom,
                adresse e-mail, numéro de téléphone, nom de l'entreprise.
              </li>
              <li>
                <strong className="text-white">Données de connexion :</strong> identifiants de compte,
                jetons d'authentification chiffrés pour les plateformes connectées (Meta/Instagram/Facebook).
              </li>
              <li>
                <strong className="text-white">Données de véhicules :</strong> informations sur les
                véhicules (marque, modèle, prix, photos, descriptions) importées depuis les plateformes
                de vente en ligne.
              </li>
              <li>
                <strong className="text-white">Données d'utilisation :</strong> statistiques d'engagement
                sur les publications (likes, commentaires, partages, portée), données de navigation
                sur le site vitrine.
              </li>
              <li>
                <strong className="text-white">Données techniques :</strong> adresse IP, type de navigateur,
                horodatages des connexions.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Finalités du traitement</h2>
            <p className="mb-3">Vos données sont traitées pour les finalités suivantes :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Gestion de votre compte et authentification.</li>
              <li>Publication automatique de contenu sur vos réseaux sociaux (Instagram, Facebook)
                via les API Meta, à votre demande.</li>
              <li>Collecte et affichage de statistiques d'engagement sur vos publications.</li>
              <li>Importation et gestion de votre stock de véhicules depuis les plateformes partenaires.</li>
              <li>Fonctionnement de votre site vitrine et mesure de son audience.</li>
              <li>Communication avec vous concernant votre compte et nos services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. Base légale du traitement</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Exécution du contrat :</strong> le traitement est
                nécessaire à la fourniture de nos services (publication, importation de véhicules,
                statistiques).
              </li>
              <li>
                <strong className="text-white">Consentement :</strong> pour la connexion de vos
                comptes de réseaux sociaux via OAuth et l'accès à vos données d'engagement.
              </li>
              <li>
                <strong className="text-white">Intérêt légitime :</strong> pour l'amélioration de
                nos services et la sécurité de la plateforme.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Partage des données</h2>
            <p className="mb-3">Vos données peuvent être partagées avec :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Meta Platforms (Facebook/Instagram) :</strong> pour
                la publication de contenu et la récupération de statistiques, conformément aux
                autorisations que vous accordez via OAuth.
              </li>
              <li>
                <strong className="text-white">Hébergeurs et sous-traitants techniques :</strong> pour
                l'hébergement de la plateforme et le stockage sécurisé des données.
              </li>
            </ul>
            <p className="mt-3">
              Nous ne vendons jamais vos données personnelles à des tiers. Nous ne partageons
              vos données qu'avec les prestataires strictement nécessaires au fonctionnement du service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">7. Sécurité des données</h2>
            <p>
              Les jetons d'accès aux plateformes tierces sont chiffrés avant stockage.
              Les communications sont protégées par HTTPS. L'accès aux données est restreint
              aux seuls utilisateurs authentifiés et aux administrateurs autorisés.
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées
              pour protéger vos données contre tout accès non autorisé, perte ou altération.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">8. Conservation des données</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Les données de compte sont conservées tant que votre compte est actif.</li>
              <li>Les jetons OAuth expirent automatiquement après 60 jours et sont renouvelés
                lors de la reconnexion.</li>
              <li>Les statistiques d'engagement sont conservées pour la durée de votre abonnement.</li>
              <li>En cas de suppression de compte, vos données personnelles sont supprimées
                dans un délai de 30 jours.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">9. Vos droits (RGPD)</h2>
            <p className="mb-3">Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Droit d'accès :</strong> obtenir une copie de vos données personnelles.</li>
              <li><strong className="text-white">Droit de rectification :</strong> corriger vos données inexactes ou incomplètes.</li>
              <li><strong className="text-white">Droit à l'effacement :</strong> demander la suppression de vos données.</li>
              <li><strong className="text-white">Droit à la portabilité :</strong> recevoir vos données dans un format structuré.</li>
              <li><strong className="text-white">Droit d'opposition :</strong> vous opposer au traitement de vos données.</li>
              <li><strong className="text-white">Droit de retrait du consentement :</strong> retirer votre consentement
                à tout moment (notamment en déconnectant vos comptes de réseaux sociaux).</li>
            </ul>
            <p className="mt-3">
              Pour exercer ces droits, contactez-nous à{' '}
              <a href="mailto:privacy@carmin.app" className="text-carmin hover:underline">privacy@carmin.app</a>.
              Nous répondrons dans un délai de 30 jours.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Données Meta (Facebook & Instagram)</h2>
            <p className="mb-3">
              Lorsque vous connectez votre compte Facebook et/ou Instagram à Carmin, nous accédons
              aux données suivantes avec votre consentement explicite :
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Informations de votre profil Instagram Business (nom d'utilisateur, photo de profil).</li>
              <li>Liste de vos Pages Facebook dont vous êtes administrateur.</li>
              <li>Publication de contenu (photos, textes) sur votre compte Instagram et votre Page Facebook.</li>
              <li>Statistiques d'engagement de vos publications (likes, commentaires, portée, impressions).</li>
            </ul>
            <p className="mt-3">
              Vous pouvez révoquer cet accès à tout moment en déconnectant vos comptes depuis
              les paramètres de Carmin ou depuis les paramètres de votre compte Facebook/Instagram.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">11. Suppression des données</h2>
            <p>
              Vous pouvez demander la suppression de toutes vos données en nous contactant
              à <a href="mailto:privacy@carmin.app" className="text-carmin hover:underline">privacy@carmin.app</a> ou
              en supprimant votre compte depuis les paramètres de l'application. Lors de la
              suppression, tous vos jetons d'accès, données de véhicules, publications et
              statistiques seront définitivement effacés.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">12. Modifications</h2>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité.
              Toute modification sera publiée sur cette page avec une date de mise à jour.
              En cas de modification substantielle, nous vous en informerons par e-mail.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">13. Contact</h2>
            <p>
              Pour toute question concernant cette politique de confidentialité ou le traitement
              de vos données personnelles, contactez-nous à :{' '}
              <a href="mailto:privacy@carmin.app" className="text-carmin hover:underline">privacy@carmin.app</a>
            </p>
            <p className="mt-3">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire
              une réclamation auprès de l'Autorité de protection des données (APD) belge :{' '}
              <a href="https://www.autoriteprotectiondonnees.be" className="text-carmin hover:underline" target="_blank" rel="noopener noreferrer">
                www.autoriteprotectiondonnees.be
              </a>
            </p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-dark-border text-center text-sm text-secondary">
          © {new Date().getFullYear()} Carmin — Belgique
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
